import { DatePipe, NgIf } from '@angular/common';
import { AfterViewInit, Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { FuseFullscreenComponent } from '@fuse/components/fullscreen';
import { FuseLoadingBarComponent } from '@fuse/components/loading-bar';
import { FuseNavigationService, FuseVerticalNavigationComponent } from '@fuse/components/navigation';
import { FuseConfirmationService } from '@fuse/services/confirmation';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { EnumViewModel, LoadcellResponse, PartLocationDto, ProductDto, SiteOrigin, Status, TestMachine, WorkOrderListDto } from 'app/api/models';
import { CalibrationService, PartService, ProductService, RepairsAndCalibrationsService, UserImageService, WorkOrderService } from 'app/api/services';
import { AppAuthService } from 'app/api/services/cookie-jwt-auth/app-auth.service';
import { AddItemDialogComponent } from 'app/core/components/add-item-dialog/add-item-dialog.component';
import { DatedCalibrationResponse, LocalStoreService } from 'app/core/services/local-store.service';
import { NavigationService } from 'app/core/services/navigation/navigation.service';
import { Navigation } from 'app/core/services/navigation/navigation.types';
import { NotificationSnackbarService } from 'app/core/services/notification-snackbar.service';
import { ThemeService } from 'app/core/services/theme/theme.service';
import { UserService } from 'app/core/services/user/user.service';
import { User } from 'app/core/services/user/user.types';
import { LanguagesComponent } from 'app/layout/common/languages/languages.component';
import { MessagesComponent } from 'app/layout/common/messages/messages.component';
import { NotificationsComponent } from 'app/layout/common/notifications/notifications.component';
import { QuickChatComponent } from 'app/layout/common/quick-chat/quick-chat.component';
import { SearchComponent } from 'app/layout/common/search/search.component';
import { ShortcutsComponent } from 'app/layout/common/shortcuts/shortcuts.component';
import { UserComponent } from 'app/layout/common/user/user.component';
import { AddEditAssetComponent } from 'app/views/admin/erp/assets/add-edit-asset/add-edit-asset.component';
import { PartLocationDialogComponent } from 'app/views/admin/pages/parts-register/part-locations/part-location-dialog/part-location-dialog.component';
import { GenericBarcodeScanDialogComponent } from 'app/views/admin/pages/production/generic-barcode-scan-dialog/generic-barcode-scan-dialog.component';
import { GenericQrScanDialogComponent } from 'app/views/admin/pages/production/generic-qr-scan-dialog/generic-qr-scan-dialog.component';
import { WorkOrderDialogComponent } from 'app/views/admin/pages/production/work-orders/work-order-dialog/work-order-dialog.component';
import { DLMAppConfirmService } from 'app/views/calibrations/shared/services/app-confirm/app-confirm.service';
import { DLMSharedModule } from 'app/views/calibrations/shared/shared.module';
import { MachineLane } from 'app/views/calibrations/views/dashboard/dlm-dashboard/dlm-dashboard.component';
import { CalibrationFormDialogComponent } from 'app/views/calibrations/views/repairs-and-calibrations/calibration-form-dialog/calibration-form-dialog.component';
import { environment } from 'environments/environment';
import { Subject, takeUntil } from 'rxjs';

@Component({
    selector     : 'classy-layout',
    templateUrl  : './classy.component.html',
    encapsulation: ViewEncapsulation.None,
    standalone   : true,
    styles       : [`
        .fuse-vertical-navigation-item-title span {white-space: nowrap;}
        .fix-top-margin {margin-top: 0 !important;}
    `],
    imports      : [
        FuseLoadingBarComponent, FuseVerticalNavigationComponent, NotificationsComponent, UserComponent, NgIf, MatTooltipModule, MatIconModule, MatButtonModule, LanguagesComponent, FuseFullscreenComponent, SearchComponent, ShortcutsComponent, MessagesComponent, RouterOutlet, QuickChatComponent,
        DLMSharedModule, WorkOrderDialogComponent, GenericQrScanDialogComponent, CalibrationFormDialogComponent, AddEditAssetComponent, MatMenuModule, PartLocationDialogComponent,
    ],
})
export class ClassyLayoutComponent implements OnInit, OnDestroy, AfterViewInit
{
    user: User;
    public currentImageId: number;
    public avatarImageUrl: string;

    env = environment;
    isScreenSmall: boolean;
    isBasic: boolean = true;
    navigation: Navigation;
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    navigationAppearance: 'default' | 'dense' = 'dense';
    isLocked: boolean = false;
    lockIcon: 'mat_solid:lock_open' | 'mat_solid:lock' = 'mat_solid:lock_open';
    private notificationCheckInterval: any;

    loadcells: LoadcellResponse[] = [];

    /**
     * Constructor
     */
    constructor(
        private _activatedRoute: ActivatedRoute,
        private router: Router,
        private _navigationService: NavigationService,
        private _userService: UserService,
        private _fuseMediaWatcherService: FuseMediaWatcherService,
        private _fuseNavigationService: FuseNavigationService,
        private appAuth: AppAuthService,
        public themeService: ThemeService,
        private userImageService: UserImageService,
        private calibrationService: CalibrationService,
        private repairsService: RepairsAndCalibrationsService,
        private snack: MatSnackBar,
        private ls: LocalStoreService,
        private notificationSnackbar: NotificationSnackbarService,
        private dialog: MatDialog,
        private assetService: ProductService,
        private confirmService: DLMAppConfirmService,
        private datePipe: DatePipe,
        private _fuseConfirmationService: FuseConfirmationService,
        private workOrderService: WorkOrderService,
        private partService: PartService,
    )
    {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for current year
     */
    get currentYear(): number
    {
        return new Date().getFullYear();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    async ngOnInit()
    {
        // Subscribe to navigation data
        this._navigationService.navigation$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((navigation: Navigation) =>
            {
                this.navigation = navigation;
            });

        this.user = this.appAuth.getUser() as any;
        this.isBasic = this.appAuth.isBasic();

        // Subscribe to media changes
        this._fuseMediaWatcherService.onMediaChange$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(({matchingAliases}) =>
            {
                // Check if the screen is small
                this.isScreenSmall = !matchingAliases.includes('md');
            });

            this.getAvatar();

        // Check for new notifications on app load
        if (this.user?.id) {
            this.notificationSnackbar.checkForNewNotifications();

            // Start periodic checking every 60 seconds
            this.notificationCheckInterval = setInterval(() => {
                this.notificationSnackbar.checkForNewNotifications();
            }, 60000); // 60 seconds
        }
    }

    getAvatar(){
        var userId = parseInt(this.user.id);

        this.userImageService.apiUserImageUserPhotosGet$Json({ userId: userId }).subscribe(res => {
          if(!res?.data) return;

          var list = res?.data;
          this.currentImageId = list[0]?.userImageId;

          if(!this.currentImageId) return;

          this.userImageService.apiUserImageGetImageUserImageIdGet({userImageId: this.currentImageId}).subscribe(image => {
            if(!image) return;

            const reader = new FileReader();
            reader.onloadend = () => {
              this.avatarImageUrl = reader.result as string;
            };
            reader.readAsDataURL(new Blob([image]));
          })
        })
      }

    ngAfterViewInit(): void {
        const mainNavigation = this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>('mainNavigation');
        mainNavigation.hoverChanged
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(open => {
                // console.log("nav bar hover", open ? 'open' : 'closed');
                if(!this.isLocked)
                    this.switchDenseNav(!open);
            });
    }

    countOfflineCalibrations = () => this.ls.getCalibrations()?.length ?? 0;

    saveOfflineCalibrations() {
        var calibrations = this.ls.getCalibrations();

        for(var calibration of calibrations)
            setTimeout(() => {
                this.calibrationService.apiCalibrationEditCalibrationPost$Json({body: calibration}).subscribe({
                    next: (res) => {
                        if (!res?.data) {
                            this.snack.open(`Error saving calibration for ${calibration.loadcellId}`, 'OK', {
                                duration: 3000
                            });
                            return;
                        }
                        this.snack.open(`Calibration for ${calibration.loadcellId} successfully synced to Nexus.`, 'OK', {
                            duration: 3000
                        });
                        this.ls.clearCalibrationsForId(calibration.calibration.calibrationId);
                        this.updateInspectedBy(calibration);
                    },
                    error: (error) => {
                        this.snack.open(`Error saving calibration for ${calibration.loadcellId}`, 'OK', {
                            duration: 3000
                        });
                    }
                });
            }, 500);
    }

    getSyncText() {
        var count = this.countOfflineCalibrations();

        var s = count != 1 ? 's' : '';
        var click = count > 0 ? ' - Click to Sync' : ''

        return `${count} Unsynced Calibration${s}${click}`;
    }

    updateInspectedBy(calibration: DatedCalibrationResponse) {
        var user = this.appAuth.getUser();
        var currentUserId = user?.id;
        if(currentUserId)
            this.repairsService.apiRepairsAndCalibrationsUpdateInspectedByPost$Json({
                employeeId: currentUserId,
                loadcellId: calibration.loadcellId,
                userName: `${user.firstName} ${user.lastName}`,
            }).subscribe();
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void
    {
        // Clear notification check interval
        if (this.notificationCheckInterval) {
            clearInterval(this.notificationCheckInterval);
        }

        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Toggle navigation
     *
     * @param name
     */
    toggleNavigation(): void
    {
        if(!this.isScreenSmall) {
            this.switchLocked(!this.isLocked);
            return;
        }

        // Get the navigation
        const mainNavigation = this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>('mainNavigation');

        if ( mainNavigation )
        {
            // Toggle the opened status
            this.switchDenseNav(false);
            this.switchLocked(true);
            mainNavigation.toggle();
        }
    }

    switchDenseNav(dense: boolean = false) {
        this.navigationAppearance = dense ? 'dense' : 'default';
    }

    switchLocked(locked: boolean) {
        this.isLocked = locked;
        this.lockIcon = locked ? 'mat_solid:lock' : 'mat_solid:lock_open';
    }

    openBarcodePopup() {
        this.dialog.open(GenericBarcodeScanDialogComponent).afterClosed().subscribe(res => {
            console.log(res);
            this.handleScanInput(res);
        });
    }

    openQrPopup() {
        this.dialog.open(GenericQrScanDialogComponent).afterClosed().subscribe(res => {
            this.handleScanInput(res);
        });
    }

    handleScanInput(res: string) {
        if(!res) return;

        //loadcell          ##
        //asset             /hire/assets/asset-details/##
        //bin/location      nexus-part-location-##
        //work order        nexus-work-order-##

        if(res.includes('nexus-work-order-')) {
            var id = +res.split('nexus-work-order-')[1];
            this.showWorkOrderPopup(id);
        }
        else if (res.includes('nexus-part-location-')) {
            var code = res.split('nexus-part-location-')[1];
            this.showLocationPopup(code);
        }
        else if (res.includes('assets/asset-details')) {
            var id = Number.parseInt(res.split('/').pop());
            this.showAssetPopup(id);
        }
        else {
            var id = +res;
            this.showLoadcellPopup(id);
        }
    }

    showWorkOrderPopup(id: number) {
        this.workOrderService.apiWorkOrderGetWorkOrderPost$Json$Response({id: id}).subscribe(res => {
            if(!res?.body?.data) return;

            var wo = res.body.data;

            const confirmation = this._fuseConfirmationService.open({
                title  : 'Work Order Scanned',
                message: `Found work order: ${wo.workOrderRef}`,
                actions: {
                    confirm: {
                        label: 'Open',
                    },
                    cancel: {
                        show: true,
                    }
                },
            });

            confirmation.afterClosed().subscribe(res => {
                if (res === 'confirmed')
                    this.openWorkOrder(wo);
            });
        })
    }

    showAssetPopup(id: number) {
        this.assetService.apiProductGetProductGet$Json$Response({productId: id}).subscribe(res => {
            if(!res?.body?.data) return;

            var asset = res.body.data;

            const confirmation = this._fuseConfirmationService.open({
                title  : 'Asset Scanned',
                message: `Found asset: ${asset.name}`,
                actions: {
                    confirm: {
                        label: 'Open',
                    },
                    cancel: {
                        show: true,
                    }
                },
            });

            confirmation.afterClosed().subscribe(res => {
                if (res === 'confirmed')
                    this.openEditProductDialog(id);
            });
        })
    }

    showLocationPopup(code: string) {
        const confirmation = this._fuseConfirmationService.open({
            title  : 'Bin Location Scanned',
            message: `Found bin: ${code}`,
            actions: {
                confirm: {
                    label: 'Open',
                },
                cancel: {
                    show: true,
                }
            },
        });

        confirmation.afterClosed().subscribe(res => {
            if (res === 'confirmed')
                this.partService.apiPartLocationsGet$Json$Response({searchTerm: code}).subscribe(res => {
                    if(!res?.body?.data?.length) return;

                    var location = res.body.data[0];
                    this.openPartLocation(location);
                });
        });
    }

    async showLoadcellPopup(id: number) {
        if(!this.loadcells.length)
            await this.loadLoadcells();

        var cell = this.loadcells.find(x => x.form10aId == id);
        if(!cell) return;

        const confirmation = this._fuseConfirmationService.open({
            title  : 'Loadcell Scanned',
            message: `Found loadcell: ${cell.bookingRef}`,
            actions: {
                confirm: {
                    label: 'Open',
                },
                cancel: {
                    show: true,
                }
            },
        });

        confirmation.afterClosed().subscribe(res => {
            if (res === 'confirmed')
                this.repairsService.apiRepairsAndCalibrationsGetStatusByFormIdGet$Json({ form10aId: id }).subscribe((statusRes) => {
                    if (!statusRes) return;

                    if (statusRes?.data == Status.InCalibration) {
                        var cell = this.loadcells.find(x => x.form10aId == id);
                        this.openCalibrationFormWithChoice(cell);
                    } else {
                        this.router.navigate(["/calibration/form-10a/form-10a"], {
                            queryParams: {
                                form10aId: id,
                            },
                        });
                    }
                });
        });
    }

    openWorkOrder(workOrder: WorkOrderListDto = null) {
        this.dialog.open(WorkOrderDialogComponent, {
            autoFocus: false,
            data: {
                workOrderId: workOrder?.workOrderId ?? null,
            }
        });
    }

    openEditProductDialog(productId: number = null) {
        var dialog = this.dialog.open(AddEditAssetComponent, {
            panelClass: "edit-product-dialog",
            data: {
                productId: productId,
            }
        });

        dialog.afterClosed().subscribe((res) => {
            if (!res) return;

            if(!res.productId) {
                this.createProduct(res);
            } else {
                this.updateProduct(res);
            }
        });
    }

    createProduct(product: ProductDto) {
        if(!product) return;

        this.assetService.apiProductAddProductPost$Json$Response({body: product}).subscribe(res =>{
            if(!res?.body?.data) return;
        });
    }

    updateProduct(product: ProductDto) {
        if(!product.productId) return;

        this.assetService.apiProductUpdateProductPut$Json$Response({body: product}).subscribe((res) => {
            if(!res?.body?.data) return;
        });
    }

    openPartLocation(currentValue: PartLocationDto = null) {
        var dialog = this.dialog.open(PartLocationDialogComponent, {
            data: {
                location: currentValue,
            }
        });

        dialog.afterClosed().subscribe((dialogRes) => {
            if (!dialogRes) return;

            var location = dialogRes as PartLocationDto;
            location.partLocationId = currentValue?.partLocationId ?? null;

            if(currentValue?.partLocationId)
              this.partService.apiPartEditLocationPut$Json$Response({body: location}).subscribe(res => {
                if(!res?.body?.data) return;
              });
            else
              this.partService.apiPartCreateLocationPost$Json$Response({body: location}).subscribe(res => {
                if(!res?.body?.data) return;
              });
        });
    }

    async loadLoadcells() {
        var res = await this.repairsService.apiRepairsAndCalibrationsLoadcellsGet$Json({ filterText: null }).toPromise();
        if(!res?.data) return;

        this.loadcells = res?.data;
        this.populateLanes();
    }

    openCalibrationFormWithChoice(cell: LoadcellResponse) {
        //show selection of available calibrations (including new)

        this.repairsService.apiRepairsAndCalibrationsLoadcellInformationGet$Json$Response({loadcellId: cell.loadcellId}).subscribe(res => {
            if(!res?.body?.data) return;

            var calibrations = res.body.data.calibrations;
            console.log("available calibrations", calibrations.map(x => x.calibrationId));

            var calibOptions = [
                <EnumViewModel> {name: 'New Calibration', value: null},
                ...calibrations.map((x, i) => <EnumViewModel> {name: `${i+1}. ${this.datePipe.transform(x.date, 'dd/MM/yyyy')}`, value: x.calibrationId})
            ];

            var calibChoiceDialog = this.dialog.open(AddItemDialogComponent, {
            data: {
                title: "Select Calibration",
                items: [
                    {name: 'calibration', type: 'select', options: calibOptions, initialValue: cell.calibrationId},
                ],
            }
            });

            calibChoiceDialog.afterClosed().subscribe((dialogRes) => {
                if (!dialogRes) return;

                var calibrationId = dialogRes.calibration;

                var dialog = this.dialog.open(CalibrationFormDialogComponent, {
                    panelClass: "calibration-form-dialog",
                    data: { loadcellId: cell.loadcellId, calibrationId: calibrationId },
                });

                dialog.afterClosed().subscribe((result) => {
                this.loadLoadcells();

                console.log("calib form close - workshop", result);

                if (result?.calibration?.isComplete) {
                    this.confirmService
                        .confirm({
                            title: `Further Calibration Required?`,
                            message: `Would you like to begin a new calibration?`,
                            confirmText: `Calibrate`,
                            cancelText: `No`,
                            isDelete: false,
                        })
                        .subscribe(async (res) => {
                            if (res) {
                                // another calibration
                                cell.calibrationId = null;
                                this.openCalibrationForm(cell);
                                return;
                            }

                        // loadLoadcells() above replaces this.loadcells with fresh server objects, so
                        // every lane change MUST be applied to freshCell (the object we actually post)
                        // and never to `cell`.
                        const freshCell = this.loadcells?.find((x) => x.loadcellId == cell.loadcellId) ?? cell;

                        if (result.setPressureTestMachine) {
                            // Park for Later: leave Servicing where it is, move Workshop to Pressure Test.
                            freshCell.machine = TestMachine.PressureTest;
                        }
                        else if (freshCell.status === Status.InCalibration) {
                            freshCell.machine = TestMachine.CalReportComplete;
                            freshCell.status = Status.CalReportComplete;
                        }

                        freshCell.isFromCalibrationEdit = true;
                        freshCell.isPressureTestEdit = result.calibration.fromPressureTest;

                        // Refetch latest RowVersion right before sending to avoid 409 from concurrent
                        // fired-and-forget writes during the calibration dialog flow.
                        await this.refreshRowVersion(freshCell);
                        this.updateLoadcells([freshCell]);

                        this.confirmService
                            .confirm({
                                title: `Calibration Complete`,
                                message: `Would you like to return to Preliminary Report?`,
                                confirmText: `Complete`,
                                cancelText: `Return to Report`,
                                isDelete: false,
                            })
                            .subscribe((complete) => {
                                if (!complete) {
                                    // go back to form 10a
                                    this.router.navigate(["/calibration/form-10a/form-10a"], {
                                        queryParams: {
                                        loadcellId: cell.loadcellId,
                                        },
                                    });
                                }
                            });
                        });
                    }
                });

            });
        });
    }

    openCalibrationForm(cell: LoadcellResponse) {
        var dialog = this.dialog.open(CalibrationFormDialogComponent, {
            panelClass: "calibration-form-dialog",
            data: { loadcellId: cell.loadcellId, calibrationId: cell.calibrationId },
        });

        dialog.afterClosed().subscribe((result) => {
            this.loadLoadcells();

            console.log("calib form close - workshop", result);

            if (result?.calibration?.isComplete) {
            this.confirmService
                .confirm({
                title: `Further Calibration Required?`,
                message: `Would you like to begin a new calibration?`,
                confirmText: `Calibrate`,
                cancelText: `No`,
                isDelete: false,
                })
                .subscribe(async (res) => {
                if (res) {
                    // another calibration
                    cell.calibrationId = null;
                    this.openCalibrationForm(cell);
                    return;
                }

                // loadLoadcells() above replaces this.loadcells with fresh server objects, so every
                // lane change MUST be applied to freshCell (the object we actually post), not `cell`.
                const freshCell = this.loadcells?.find((x) => x.loadcellId == cell.loadcellId) ?? cell;

                if (result.setPressureTestMachine) {
                    // Park for Later: leave Servicing where it is, move Workshop to Pressure Test.
                    freshCell.machine = TestMachine.PressureTest;
                }
                else if (freshCell.status === Status.InCalibration) {
                    freshCell.machine = TestMachine.CalReportComplete;
                    freshCell.status = Status.CalReportComplete;
                }

                freshCell.isFromCalibrationEdit = true;
                freshCell.isPressureTestEdit = result.calibration.fromPressureTest;

                // Refetch latest RowVersion right before sending to avoid 409 from concurrent
                // fired-and-forget writes during the calibration dialog flow.
                await this.refreshRowVersion(freshCell);
                this.updateLoadcells([freshCell]);

                this.confirmService
                    .confirm({
                    title: `Calibration Complete`,
                    message: `Would you like to return to Preliminary Report?`,
                    confirmText: `Complete`,
                    cancelText: `Return to Report`,
                    isDelete: false,
                    })
                    .subscribe((complete) => {
                    if (!complete) {
                        // go back to form 10a
                        this.router.navigate(["/calibration/form-10a/form-10a"], {
                        queryParams: {
                            loadcellId: cell.loadcellId,
                        },
                        });
                    }
                    });
                });
            }
        });
    }

    private indexCells = (cells: LoadcellResponse[]) =>
        cells.forEach((value, i) => (value.listPosition = i));

    // Pull the latest RowVersion from the server right before saving so concurrent fired-and-forget
    // writes from the calibration form (e.g. updateInspectedBy) don't cause a 409 on our update.
    private async refreshRowVersion(cell: LoadcellResponse): Promise<void> {
        if (cell?.loadcellId == null) return;
        try {
            const latest = await this.repairsService
                .apiRepairsAndCalibrationsLoadcellInformationGet$Json({ loadcellId: cell.loadcellId })
                .toPromise();
            const latestRv = (latest?.data?.loadcell as any)?.rowVersion;
            if (latestRv) (cell as any).rowVersion = latestRv;
        } catch {
            // Network/refetch failure: fall back to whatever rowVersion we have.
        }
    }

    updateLoadcells(overrideList: LoadcellResponse[] = null) {
        //index the 'T' lanes
        var one = this.machineLanes.filter((x) => x.machine == TestMachine.One)[0];
        var sixty = this.machineLanes.filter(
          (x) => x.machine == TestMachine.Sixty
        )[0];
        var hundred = this.machineLanes.filter(
          (x) => x.machine == TestMachine.OneHundred
        )[0];
        var threehundred = this.machineLanes.filter(
          (x) => x.machine == TestMachine.ThreeHundred
        )[0];

        this.indexCells(one.cells);
        this.indexCells(sixty.cells);
        this.indexCells(hundred.cells);
        this.indexCells(threehundred.cells);

        var otherLanes = this.machineLanes.filter((x) =>
          [
            TestMachine.Unassigned,
            TestMachine.GoAhead,
            TestMachine.Other,
            TestMachine.Vulcan,
            TestMachine.CalReportComplete,
            TestMachine.PressureTest,
            TestMachine.SalesToProductionHandover,
          ].includes(x.machine)
        );

        //reset the others
        otherLanes.forEach((lane) => {
          lane?.cells
            ?.filter((x) => x != null)
            .forEach((x) => (x.listPosition = null));
        });

        var user = this.appAuth.getUser();
        this.repairsService
          .apiRepairsAndCalibrationsEditLoadcellsPost$Json({ body: overrideList ?? this.loadcells, userName: `${user.firstName} ${user.lastName}`, siteOrigin: SiteOrigin.LoadcellInformation })
          .subscribe({
            next: (res) => {
              if (overrideList) {
                for (const updated of res.data) {
                  const idx = this.loadcells.findIndex(c => c.loadcellId === updated.loadcellId);
                  if (idx >= 0) Object.assign(this.loadcells[idx], updated);
                }
              } else {
                this.loadcells = res.data;
              }
              this.populateLanes();
            }
          });
    }

    populateLanes() {
        this.machineLanes.forEach((machineLane) => {
        machineLane.cells = this.loadcells?.filter(
            (x) => x.machine == machineLane.machine
        );
        if (
            [
            TestMachine.One,
            TestMachine.Sixty,
            TestMachine.OneHundred,
            TestMachine.ThreeHundred,
            ].includes(machineLane.machine)
        ) {
            //sort by list pos
            machineLane.cells = machineLane.cells.sort(
            (a, b) => a.listPosition - b.listPosition
            );
        }
        });
    }

    machineLanes: MachineLane[] = [
        {
          id: 0,
          name: "Unassigned",
          machine: TestMachine.Unassigned,
          colour: "#8bba2e",
          expanded: true,
          cells: [],
        },
        {
          id: 1,
          name: "Go Ahead",
          machine: TestMachine.GoAhead,
          colour: "#9bf870",
          expanded: true,
          cells: [],
        },
        {
          id: 10,
          name: "Sales to Production Handover",
          machine: TestMachine.SalesToProductionHandover,
          colour: "#d094e7",
          expanded: true,
          cells: [],
        },
        {
          id: 2,
          name: "25T",
          machine: TestMachine.One,
          colour: "#44bbb7",
          expanded: true,
          cells: [],
        },
        {
          id: 3,
          name: "60T",
          machine: TestMachine.Sixty,
          colour: "#d657a5",
          expanded: true,
          cells: [],
        },
        {
          id: 4,
          name: "100T",
          machine: TestMachine.OneHundred,
          colour: "#ef8d20",
          expanded: true,
          cells: [],
        },
        {
          id: 5,
          name: "300T",
          machine: TestMachine.ThreeHundred,
          colour: "#6d3888",
          expanded: true,
          cells: [],
        },
        {
          id: 6,
          name: "Outdoor",
          machine: TestMachine.Vulcan,
          colour: "#fbc51b",
          expanded: true,
          cells: [],
        },
        {
          id: 9,
          name: "Pressure Test",
          machine: TestMachine.PressureTest,
          colour: "#c1d007",
          expanded: true,
          cells: [],
        },
        {
          id: 7,
          name: "Other",
          machine: TestMachine.Other,
          colour: "#0081ff",
          expanded: true,
          cells: [],
        },
        {
          id: 8,
          name: "Cal / Report Complete",
          machine: TestMachine.CalReportComplete,
          colour: "#3e8959",
          expanded: true,
          cells: [],
        },
    ];
}
